// import React, { useCallback, useEffect, useState } from "react";
// import { User } from "../types";
// import { getUserData } from "../services/api";
// import { supabase } from "../utils/supabase";
// import { User as UserSupabase } from "@supabase/supabase-js";
// import { LoginPage } from "../components/admin/LoginPage";
// import { AdminPanel } from "../components/admin/AdminPanel";

// const AdminPage: React.FC = () => {
//   const [isAuthenticated, setIsAuthenticated] = useState(false);
//   const [checkingSession, setCheckingSession] = useState(true);
//   const [currentUser, setCurrentUser] = useState<User | null>(null);

//   const loadCurrentUser = useCallback(async (userEmail: string) => {
//     try {
//       const user = await getUserData(userEmail);

//       setCurrentUser(user);
//     } catch (error) {
//       console.error("Error loading user:", error);
//     }
//   }, []);

//   const checkSession = useCallback(async () => {
//     try {
//       const mockSession = await supabase.auth.getSession();

//       if (mockSession.data.session) {
//         setIsAuthenticated(true);
//         await loadCurrentUser(mockSession.data.session.user.email!);
//       }
//     } catch (error) {
//       console.error("Error checking session:", error);
//     } finally {
//       setCheckingSession(false);
//     }
//   }, [loadCurrentUser]);

//   useEffect(() => {
//     checkSession();
//   }, [checkSession]);

//   const handleAuthentication = async (data: UserSupabase) => {
//     setIsAuthenticated(true);
//     await loadCurrentUser(data.email!);
//   };

//   const handleLogout = async () => {
//     await supabase.auth.signOut();
//     setIsAuthenticated(false);
//     setCurrentUser(null);
//   };

//   if (checkingSession) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
//         <div className="text-center">
//           <div className="animate-spin text-5xl mb-4">⏳</div>
//           <p className="text-gray-600 text-lg">Verificando sesión...</p>
//         </div>
//       </div>
//     );
//   }

//   if (!isAuthenticated) {
//     return <LoginPage setAuthentication={handleAuthentication} />;
//   }

//   return <AdminPanel currentUser={currentUser!} handleLogout={handleLogout} />;
// };

// export default AdminPage;

import React, { useCallback, useEffect, useState } from "react";
import { Application, BlankApplication, User } from "../types";
import {
  deleteApplication,
  downloadBusinessLogo,
  fetchApplications,
  getIsClosed,
  getUserData,
  setIsClosed,
  updateApplication,
} from "../services/api";
import { exportToExcel } from "../utils/exportUtils";
import { TbReload } from "react-icons/tb";
import { DeleteModal } from "../components/modals/DeleteModal";
import EditModal from "../components/modals/EditModal";
import { ZipModal } from "../components/modals/ZipModal";
import { UserManagementModal } from "../components/modals/UserManagmentModal";
// import ChartsDashboard from "./Charts";
import ChartsDashboard from "../components/admin/Charts";
import { supabase } from "../utils/supabase";
import { LoginPage } from "../components/admin/LoginPage";
import { User as UserSupabase } from "@supabase/supabase-js";

type MenuItem = {
  id: string;
  label: string;
  icon: string;
  roles?: User["role"][];
};

const AdminPanel: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<string>("todos");
  const [selectedStudent, setSelectedStudent] =
    useState<Application>(BlankApplication);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );
  const [showZipModal, setShowZipModal] = useState<boolean>(false);
  const [showGafetesModal, setShowGafetesModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalApplication, setModalApplication] =
    useState<Application>(BlankApplication);
  const [showUserManagement, setShowUserManagement] = useState<boolean>(false);
  const [selectedApplication, setSelectedApplication] =
    useState<Application>(BlankApplication);
  const [activeSection, setActiveSection] = useState<string>("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isFormClosed, setIsFormClosed] = useState<boolean>(false);

  const loadCurrentUser = useCallback(async (userEmail: string) => {
    try {
      const user = await getUserData(userEmail);

      setCurrentUser(user);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const mockSession = await supabase.auth.getSession();

      if (mockSession.data.session) {
        setIsAuthenticated(true);
        await loadCurrentUser(mockSession.data.session.user.email!);
      }
    } catch (error) {
      console.error("Error checking session:", error);
    } finally {
      setCheckingSession(false);
    }
  }, [loadCurrentUser]);

  const menuItems: MenuItem[] = [
    { id: "dashboard", label: "Panel Principal", icon: "📊" },
    { id: "applications", label: "Solicitudes", icon: "📝" },
    { id: "reports", label: "Reportes", icon: "📈" },
    { id: "documents", label: "Documentos", icon: "📄" },
    {
      id: "users",
      label: "Gestión de Usuarios",
      icon: "👥",
      roles: ["super_admin"],
    },
  ];

  const loadApplications = useCallback(async () => {
    setIsLoading(true);
    const data = await fetchApplications();
    setApplications(data);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      await loadApplications();
      const closedStatus = await getIsClosed();
      setIsFormClosed(closedStatus);
    })();
  }, [loadApplications]);

  const handleEdit = useCallback((Application: Application) => {
    setSelectedStudent(Application);
    setIsEditModalOpen(true);
  }, []);

  const handleSave = useCallback(async (updatedStudent: Application) => {
    await updateApplication(updatedStudent.id!, updatedStudent);
    setApplications((prev) =>
      prev.map((Application) =>
        Application.id === updatedStudent.id ? updatedStudent : Application
      )
    );
  }, []);

  const confirmDelete = async (id: string) => {
    await deleteApplication(id);
    setApplications((prev) =>
      prev.filter((Application) => Application.id !== id)
    );
    setShowDeleteConfirm(null);
  };

  const filteredApplications = applications.filter((Application) => {
    const matchesSearch =
      Application.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      Application.studentId.includes(searchTerm) ||
      Application.businessName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "todos" || Application.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: Application["status"]) => {
    switch (status) {
      case "approved":
        return "text-green-700 bg-green-50 border-green-200";
      case "rejected":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-yellow-700 bg-yellow-50 border-yellow-200";
    }
  };

  const downloadLogo = async (logoUrl: string, businessName: string) => {
    const blob = await downloadBusinessLogo(logoUrl.split("/").pop() || "");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `logo-${businessName.replace(/\s+/g, "-").toLowerCase()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getRoleBadge = (role: User["role"]) => {
    switch (role) {
      case "super_admin":
        return (
          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
            Super Admin
          </span>
        );
      case "evaluator":
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
            Evaluador
          </span>
        );
      case "viewer":
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
            Visualizador
          </span>
        );
    }
  };

  const canEdit = () =>
    currentUser?.role === "super_admin" || currentUser?.role === "evaluator";
  const canDelete = () => currentUser?.role === "super_admin";

  const hasAccessToMenuItem = (item: MenuItem) => {
    if (!item.roles) return true;
    return item.roles.includes(currentUser!.role);
  };

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const handleAuthentication = async (data: UserSupabase) => {
    setIsAuthenticated(true);
    await loadCurrentUser(data.email!);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const handleClosed = async (value: boolean) => {
    await setIsClosed(value);
    setIsFormClosed(value);
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin text-5xl mb-4">⏳</div>
          <p className="text-gray-600 text-lg">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage setAuthentication={handleAuthentication} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  const renderDashboard = () => (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Panel de Control
        </h2>
        <button
          onClick={() => handleClosed(!isFormClosed)}
          className="text-sm text-white bg-[var(--uat-accent)] px-3 py-1 rounded-lg hover:bg-[var(--uat-gold)] transition-colors"
        >
          {isFormClosed ? "Abrir" : "Cerrar"} inscripciones
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border border-gray-300 shadow-sm hover:shadow-md rounded-xl p-6 transition-all duration-200">
          <p className="text-sm text-gray-500 font-medium mb-2">
            Total de solicitudes
          </p>

          <p className="text-4xl font-bold text-blue-700">
            {applications.length}
          </p>
        </div>

        <div className="bg-white border border-gray-300 shadow-sm hover:shadow-md rounded-xl p-6 transition-all duration-200">
          <p className="text-sm text-gray-500 font-medium mb-2">Pendientes</p>
          <p className="text-4xl font-bold text-yellow-700">
            {applications.filter((s) => s.status === "pending").length}
          </p>
        </div>

        <div className="bg-white border border-gray-300 shadow-sm hover:shadow-md rounded-xl p-6 transition-all duration-200">
          <p className="text-sm text-gray-500 font-medium mb-2">Aprobados</p>
          <p className="text-4xl font-bold text-green-700">
            {applications.filter((s) => s.status === "approved").length}
          </p>
        </div>

        <div className="bg-white border border-gray-300 shadow-sm hover:shadow-md rounded-xl p-6 transition-all duration-200">
          <p className="text-sm text-gray-500 font-medium mb-2">Rechazados</p>
          <p className="text-4xl font-bold text-red-700">
            {applications.filter((s) => s.status === "rejected").length}
          </p>
        </div>
      </div>

      <ChartsDashboard applications={applications} />
    </div>
  );

  const renderApplications = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Gestión de Solicitudes
      </h2>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Buscar por nombre, matrícula o emprendimiento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[var(--uat-accent)]"
          />
        </div>
        <TbReload
          size={5}
          color="black"
          onClick={() => loadApplications()}
          className="w-10 h-10 rounded-lg border border-gray-200 p-2 cursor-pointer hover:bg-gray-50"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 rounded-lg border border-gray-200 bg-white"
        >
          <option value="todos">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="approved">Aprobados</option>
          <option value="rejected">Rechazados</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estudiante
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Emprendimiento
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredApplications
              .sort(
                (a, b) =>
                  new Date(a.registrationDate).getTime() -
                  new Date(b.registrationDate).getTime()
              )
              .map((Application) => (
                <tr
                  key={Application.id}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    selectedApplication.id === Application.id
                      ? "bg-blue-50"
                      : ""
                  }`}
                  onClick={() => setSelectedApplication(Application)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {selectedApplication.id === Application.id
                            ? "⭐ "
                            : ""}
                          {Application.fullName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {Application.studentId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {Application.businessLogo && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowModal(true);
                            setModalApplication(Application);
                          }}
                        >
                          <img
                            src={Application.businessLogo}
                            alt="Logo"
                            className="h-8 w-8 rounded object-contain bg-gray-50"
                          />
                        </button>
                      )}
                      <span className="text-sm text-gray-900">
                        {Application.businessName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(
                      Application.registrationDate
                    ).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(
                        Application.status
                      )}`}
                    >
                      {Application.status}
                    </span>
                    {Application.observations && (
                      <p className="text-xs text-gray-500 mt-1">
                        Obs: {Application.observations}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {selectedApplication.id && (
        <div className="bg-white mt-4 border border-gray-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-center justify-start gap-3">
            {canEdit() && (
              <button
                onClick={() => handleEdit(selectedApplication)}
                className="flex items-center gap-2 bg-gradient-to-r from-[var(--uat-accent)] to-orange-500 text-white font-medium px-4 py-2 rounded-lg shadow-sm hover:shadow-md hover:scale-[1.03] transition-transform"
              >
                ✏️ Editar
              </button>
            )}

            {canDelete() && (
              <button
                onClick={() => setShowDeleteConfirm(selectedApplication.id!)}
                className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-500 text-white font-medium px-4 py-2 rounded-lg shadow-sm hover:shadow-md hover:scale-[1.03] transition-transform"
              >
                🗑️ Eliminar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderReports = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Reportes y Análisis
      </h2>
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white border border-gray-300 rounded-xl p-6 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            📊 Estadísticas Generales
          </h3>
          <p className="text-gray-600 mb-4">
            Visualiza métricas clave y estadísticas del sistema
          </p>
          <ChartsDashboard applications={applications} />
        </div>

        <div className="bg-white border border-gray-300 rounded-xl p-6 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            📈 Análisis de Tendencias
          </h3>
          <p className="text-gray-600 mb-4">
            Registros por fecha y estado de solicitudes
          </p>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tasa de aprobación:</span>
              <span className="text-lg font-bold text-green-600">
                {applications.length > 0
                  ? (
                      (applications.filter((a) => a.status === "approved")
                        .length /
                        applications.length) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tasa de rechazo:</span>
              <span className="text-lg font-bold text-red-600">
                {applications.length > 0
                  ? (
                      (applications.filter((a) => a.status === "rejected")
                        .length /
                        applications.length) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Gestión de Documentos
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => exportToExcel(filteredApplications)}
          className="bg-white border-2 border-[var(--uat-accent)] hover:bg-[var(--uat-accent)] hover:text-white text-gray-800 rounded-xl p-6 transition-all duration-200 hover:shadow-lg"
        >
          <div className="text-4xl mb-3">📊</div>
          <h3 className="text-lg font-semibold mb-2">Exportar CSV</h3>
          <p className="text-sm opacity-75">
            Descarga los datos en formato Excel
          </p>
        </button>

        <button
          onClick={() => setShowZipModal(true)}
          className="bg-white border-2 border-[var(--uat-gold)] hover:bg-[var(--uat-gold)] hover:text-white text-gray-800 rounded-xl p-6 transition-all duration-200 hover:shadow-lg"
        >
          <div className="text-4xl mb-3">📄</div>
          <h3 className="text-lg font-semibold mb-2">Generar Cartas</h3>
          <p className="text-sm opacity-75">
            Crea documentos oficiales en lote
          </p>
        </button>

        <button
          onClick={() => setShowGafetesModal(true)}
          className="bg-white border-2 border-purple-600 hover:bg-purple-600 hover:text-white text-gray-800 rounded-xl p-6 transition-all duration-200 hover:shadow-lg"
        >
          <div className="text-4xl mb-3">🎫</div>
          <h3 className="text-lg font-semibold mb-2">Generar Gafetes</h3>
          <p className="text-sm opacity-75">
            Crea identificaciones para participantes
          </p>
        </button>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Gestión de Usuarios
      </h2>
      <div className="bg-white border border-gray-300 rounded-xl p-6">
        <button
          onClick={() => setShowUserManagement(true)}
          className="bg-gradient-to-r from-purple-600 to-purple-500 text-white font-medium px-6 py-3 rounded-lg shadow-sm hover:shadow-md hover:scale-[1.03] transition-transform"
        >
          👥 Administrar Usuarios
        </button>
        <p className="text-gray-600 mt-4">
          Gestiona los permisos y accesos de los usuarios del sistema
        </p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboard();
      case "applications":
        return renderApplications();
      case "reports":
        return renderReports();
      case "documents":
        return renderDocuments();
      case "users":
        return renderUsers();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 z-40">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <span className="text-2xl">{isSidebarOpen ? "✕" : "☰"}</span>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[var(--uat-blue)]">
                Panel de Administración
              </h1>
              <p className="text-sm text-gray-600">
                Feria de Emprendedores UAT
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Sesión activa</p>
              <p className="text-sm font-medium text-gray-900">
                {currentUser!.email}
              </p>
              <div className="mt-1">{getRoleBadge(currentUser!.role)}</div>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            isSidebarOpen ? "w-64" : "w-0"
          } bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden sticky top-16 h-[calc(100vh-4rem)]`}
        >
          <nav className="p-4">
            <ul className="space-y-2">
              {menuItems
                .filter((item) => hasAccessToMenuItem(item))
                .map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeSection === item.id
                          ? "bg-[var(--uat-accent)] text-white shadow-md"
                          : "hover:bg-gray-100 text-gray-700"
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">{renderContent()}</div>
        </main>
      </div>

      {/* Modals */}
      <EditModal
        Application={selectedStudent}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedStudent(BlankApplication);
        }}
        onSave={handleSave}
      />

      {showDeleteConfirm && (
        <DeleteModal
          onCancel={() => setShowDeleteConfirm(null)}
          onConfirm={() => confirmDelete(selectedApplication.id!)}
        />
      )}

      {showZipModal && (
        <ZipModal
          onClose={() => setShowZipModal(false)}
          applications={filteredApplications}
          letters={true}
        />
      )}

      {showGafetesModal && (
        <ZipModal
          onClose={() => setShowGafetesModal(false)}
          applications={filteredApplications}
        />
      )}

      {showUserManagement && (
        <UserManagementModal
          onClose={() => setShowUserManagement(false)}
          currentUserId={currentUser?.id || ""}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="rounded-t-2xl px-6 pt-8 text-center">
              <h3 className="text-2xl font-bold text-black">
                {modalApplication.businessName}
              </h3>
            </div>

            <div className="p-6">
              <img
                src={modalApplication.businessLogo}
                alt={modalApplication.businessName}
                className="w-full h-auto mb-4 rounded-lg"
              />
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-center gap-3">
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                onClick={() =>
                  downloadLogo(
                    modalApplication.businessLogo,
                    modalApplication.businessName
                  )
                }
              >
                Download
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
